import clsx from 'clsx';
import { addDays, addMonths, endOfMonth, isAfter, isBefore, isSameDay, startOfMonth, startOfWeek } from 'date-fns';
import type { ReactNode } from 'react';
import { ns, useLocale, useT } from '@/i18n';
import { formatDayLong, formatMonthYear, formatWeekdayNarrow } from '@/lib/format';
import { Icon } from '../Icon/Icon';
import styles from './Calendar.module.css';

export interface CalendarProps {
  /** Any day in the month to show. */
  month: Date;
  /** Selected start (and end for a range). */
  start: Date | null;
  end?: Date | null;
  onPick: (day: Date) => void;
  /** Days that cannot be picked (past days, unavailable days). */
  isDisabled?: (day: Date) => boolean;
  /** Text on the right of the month title (e.g. «Choisissez le retour»). */
  hint?: ReactNode;
  /** Enables the previous/next month buttons. */
  onMonthChange?: (month: Date) => void;
  /** Optional legend under the grid. */
  legend?: ReactNode;
  className?: string;
}

/** Monday first: «L M M J V S D» / «M T W T F S S». */
const MONDAY = startOfWeek(new Date(2026, 0, 7), { weekStartsOn: 1 });
const WEEK = Array.from({ length: 7 }, (_, i) => addDays(MONDAY, i));

/** Month grid with range highlighting (`.cal`, `.cd`). Monday first, labels in the current language. */
export function Calendar({
  month,
  start,
  end = null,
  onPick,
  isDisabled,
  hint,
  onMonthChange,
  legend,
  className,
}: CalendarProps) {
  const t = useT(ns.common);
  const locale = useLocale();
  const first = startOfMonth(month);
  const last = endOfMonth(month);
  const lead = (first.getDay() + 6) % 7;
  const days: (Date | null)[] = [...Array.from({ length: lead }, () => null)];
  for (let d = first; !isAfter(d, last); d = addDays(d, 1)) days.push(d);
  const title = formatMonthYear(first, locale);
  const titleCap = title.charAt(0).toUpperCase() + title.slice(1);

  return (
    <div className={className}>
      <div className={styles.head}>
        <div className={styles.titleRow}>
          {onMonthChange && (
            <button
              type="button"
              className={styles.nav}
              aria-label={t('calendar.previous')}
              onClick={() => onMonthChange(addMonths(first, -1))}
            >
              <Icon name="chevronLeft" size={16} strokeWidth={2.2} />
            </button>
          )}
          <b className="t-display">{titleCap}</b>
          {onMonthChange && (
            <button
              type="button"
              className={styles.nav}
              aria-label={t('calendar.next')}
              onClick={() => onMonthChange(addMonths(first, 1))}
            >
              <Icon name="chevronRight" size={16} strokeWidth={2.2} />
            </button>
          )}
        </div>
        {hint && <span className="t-small t-muted">{hint}</span>}
      </div>
      <div className={styles.cal} role="grid" aria-label={titleCap}>
        {WEEK.map((w, i) => (
          <span key={i} className={styles.dw} aria-hidden="true">
            {formatWeekdayNarrow(w, locale)}
          </span>
        ))}
        {days.map((d, i) => {
          if (!d) return <span key={`b${i}`} className={clsx(styles.cd, styles.blank)} aria-hidden="true" />;
          const off = isDisabled?.(d) ?? false;
          const isStart = !!start && isSameDay(d, start);
          const isEnd = !!end && isSameDay(d, end);
          const inRange = !!start && !!end && isAfter(d, start) && isBefore(d, end);
          const hasRange = !!start && !!end && !isSameDay(start, end);
          return (
            <button
              key={d.toISOString()}
              type="button"
              disabled={off}
              aria-pressed={isStart || isEnd}
              aria-label={off ? t('calendar.unavailable', { day: formatDayLong(d, locale) }) : formatDayLong(d, locale)}
              className={clsx(
                styles.cd,
                inRange && styles.mid,
                hasRange && isStart && styles.s0,
                hasRange && isEnd && styles.e0,
                (isStart || isEnd) && styles.sel,
                off && styles.off,
              )}
              onClick={() => onPick(d)}
            >
              <span>{d.getDate()}</span>
            </button>
          );
        })}
      </div>
      {legend && <div className={styles.legend}>{legend}</div>}
    </div>
  );
}

/** One legend entry: a coloured dot and its text. */
export function CalendarLegendItem({ color, children }: { color: string; children: ReactNode }) {
  return (
    <span>
      <i style={{ background: color }} />
      {children}
    </span>
  );
}
